import React, { useState, useEffect } from 'react';
import { Container, Table, Badge, Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { userAPI } from '../utils/api';

const TYPE_MAP = {
  'User': { label: '일반 유저', variant: 'secondary' },
  'Manager': { label: '사찰 관리자', variant: 'success' },
  'Admin': { label: '최고 관리자', variant: 'danger' },
};

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [userRes, allUsersRes] = await Promise.all([
        userAPI.getProfile().catch(() => null),
        userAPI.getAllUsers()
      ]);
      
      if (userRes && userRes.data) {
        setCurrentUser(userRes.data);
      }
      
      let data = allUsersRes.data.results || allUsersRes.data;
      // 가입일 역순 정렬
      if (Array.isArray(data)) {
        data.sort((a, b) => new Date(b.date_joined || 0) - new Date(a.date_joined || 0));
      }
      setUsers(data);
    } catch (err) {
      console.error('유저 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleTypeChange = async (userId, newType) => {
    if (!window.confirm(`이 유저의 권한을 ${TYPE_MAP[newType]?.label}(으)로 변경하시겠습니까?`)) return;
    try {
      await userAPI.updateUserById(userId, { type: newType });
      alert('권한이 변경되었습니다.');
      fetchUsers();
    } catch (err) {
      alert('권한 변경에 실패했습니다.');
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`"${userName}" 유저를 정말 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) return;
    try {
      await userAPI.deleteUserById(userId);
      alert('유저가 삭제되었습니다.');
      fetchUsers();
    } catch (err) {
      alert('유저 삭제에 실패했습니다.');
    }
  };

  if (loading) {
    return (
      <div className="au-loading">
        <span className="au-dharma-spin">☸</span>
        <p>불러오는 중…</p>
      </div>
    );
  }

  if (currentUser?.type !== 'Admin') {
    return <Container className="py-5 text-center">최고 관리자 권한이 필요합니다.</Container>;
  }

  return (
    <div className="au-wrap">
      <div className="tal-hero">
        <div className="tal-hero-bg">☸</div>
        <Container className="tal-hero-inner">
          <div className="tal-tabs">
            <button className="tal-tab" onClick={() => navigate('/admin/temples')}>사찰 관리</button>
            <button className="tal-tab" onClick={() => navigate('/admin/orders')}>주문 관리</button>
            <button className="tal-tab" onClick={() => navigate('/admin/orders?tab=settlements')}>정산 관리</button>
            <button className="tal-tab active">유저 관리</button>
          </div>
          <h1 className="tal-hero-title">유저 관리 (최고관리자)</h1>
          <p className="tal-hero-sub">전체 유저 목록을 조회하고 권한을 설정합니다.</p>
        </Container>
      </div>

      <Container className="py-4">
        <div className="au-table-wrap">
          <Table className="tal-table" hover style={{ margin: 0, width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr>
                <th style={{ padding: '15px', fontSize: '0.8rem', color: '#6e8a84', borderBottom: '1px solid #e4edec', backgroundColor: '#fcfdfd' }}>이름</th>
                <th style={{ padding: '15px', fontSize: '0.8rem', color: '#6e8a84', borderBottom: '1px solid #e4edec', backgroundColor: '#fcfdfd' }}>아이디(유형)</th>
                <th style={{ padding: '15px', fontSize: '0.8rem', color: '#6e8a84', borderBottom: '1px solid #e4edec', backgroundColor: '#fcfdfd' }}>전화번호</th>
                <th style={{ padding: '15px', fontSize: '0.8rem', color: '#6e8a84', borderBottom: '1px solid #e4edec', backgroundColor: '#fcfdfd' }}>현재 권한</th>
                <th style={{ padding: '15px', fontSize: '0.8rem', color: '#6e8a84', borderBottom: '1px solid #e4edec', backgroundColor: '#fcfdfd' }}>가입일</th>
                <th style={{ padding: '15px', fontSize: '0.8rem', color: '#6e8a84', borderBottom: '1px solid #e4edec', backgroundColor: '#fcfdfd', minWidth: '220px' }}>관리</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id}>
                  <td style={{ padding: '15px', borderBottom: '1px solid #f0f5f5', verticalAlign: 'middle', fontWeight: 'bold' }}>{u.name || '이름 없음'}</td>
                  <td style={{ padding: '15px', borderBottom: '1px solid #f0f5f5', verticalAlign: 'middle' }}>
                    {u.kakao_id ? (
                      <Badge bg="warning" text="dark" style={{ marginRight: '6px' }}>카카오</Badge>
                    ) : (
                      <Badge bg="info" style={{ marginRight: '6px' }}>일반</Badge>
                    )}
                    {u.username}
                  </td>
                  <td style={{ padding: '15px', borderBottom: '1px solid #f0f5f5', verticalAlign: 'middle' }}>{u.phone || '전화번호 없음'}</td>
                  <td style={{ padding: '15px', borderBottom: '1px solid #f0f5f5', verticalAlign: 'middle' }}>
                    <Badge bg={TYPE_MAP[u.type]?.variant || 'secondary'}>
                      {TYPE_MAP[u.type]?.label || u.type}
                    </Badge>
                  </td>
                  <td style={{ padding: '15px', borderBottom: '1px solid #f0f5f5', verticalAlign: 'middle' }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                  <td style={{ padding: '15px', borderBottom: '1px solid #f0f5f5', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <Form.Select 
                        size="sm" 
                        style={{ width: '130px' }}
                        value={u.type}
                        onChange={(e) => handleTypeChange(u.id, e.target.value)}
                      >
                        <option value="User">일반 유저</option>
                        <option value="Manager">사찰 관리자</option>
                        <option value="Admin">최고 관리자</option>
                      </Form.Select>
                      <Button variant="outline-danger" size="sm" onClick={() => handleDelete(u.id, u.name || u.username)}>삭제</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Container>
      <style jsx="true">{`
        .au-wrap { min-height: 100vh; background: #f8fbfb; padding-bottom: 100px; }
        .au-table-wrap { overflow-x: auto; background: #fff; border-radius: 8px; border: 1px solid #e4edec; }
        .au-loading { text-align: center; padding: 100px 0; color: #6e8a84; }
        .au-dharma-spin { display: inline-block; font-size: 2rem; animation: spin 4s linear infinite; margin-bottom: 15px; color: #009590; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
        
        .tal-hero { background: linear-gradient(135deg, #e5f4f4 0%, #d5efef 100%); padding: 50px 0 40px; position: relative; overflow: hidden; }
        .tal-hero-bg { position: absolute; right: 10%; top: -20%; font-size: 20rem; color: #fff; z-index: 0; pointer-events: none; opacity: 0.3; transform: rotate(15deg); }
        .tal-hero-inner { position: relative; z-index: 1; }
        .tal-hero-title { font-size: 2.2rem; font-weight: 800; color: #009590; margin-bottom: 12px; letter-spacing: -0.02em; }
        .tal-hero-sub { color: #5a7570; font-size: 1.05rem; margin: 0; }
        
        .tal-tabs { display: flex; gap: 4px; margin-bottom: 24px; }
        .tal-tab { border: none; background: rgba(0,0,0,0.05); padding: 8px 20px; border-radius: 4px; font-size: 0.88rem; color: #6e8a84; font-weight: 600; cursor: pointer; transition: all 0.2s; }
        .tal-tab.active { background: #009590; color: #fff; }
      `}</style>
    </div>
  );
};

export default AdminUsers;
